import React, { useState, useEffect } from "react";
import { useParams } from "react-router";
import moment from "moment-timezone";

import PatientProfile from "./PatientProfile";
import { ApiCallResult, useAPI } from "services/Api";
import { downloadReports } from "reports/ReportsDownloader";
import { navigate } from "utility/NavigationService";
import { renderLoader } from "utility/LoaderUtils";
import { useNotificationManager } from "notifications/NotificationManager";

import * as R from "reports/Report"

const PatientReportReview = ({ componentName = "[PatientReportReview]" }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [loadingMessage, setLoadingMessage] = useState("Retrieving Outcome Reports");
    const [outcomeReports, setOutcomeReports] = useState(null);
    const [patientName, setPatientName] = useState(null);

    const API = useAPI();
    const notificationManager = useNotificationManager();
    const patientId = Number(useParams().id);

    // ---=== Utilities ===--- //

    const goToReportCreate = () => {
        void navigate(`/patient/${patientId}/outcomes/true`);
    };

    const goToReportReview = (report) => {
        void navigate(`/outcomes/${report.id}/${report.status === "Not Submitted"}`);
    };

    const onPatientData = (patientData) => {
        if (patientData) {
            setPatientName(`${patientData.first_name ? patientData.first_name : ""} ${patientData.last_name ? patientData.last_name : ""}`);
        }
    };

    // ---=== KarunaCLOUD ===--- //

    const api_GetOutcomeReports = async () => {
        notificationManager.clearAllNotifications();
        setIsLoading(true);
        setLoadingMessage("Retrieving Outcome Reports");

        const result: ApiCallResult = await API.api_Get(
            componentName,
            `/outcome_reports/?patient_id=${patientId}`,
            notificationManager,
            (response) => {
                let reports = response.data.results;
                reports = reports.sort(
                    (a, b) => {
                        return moment(a.prepared_date).isBefore(moment(b.prepared_date)) ? 1 : -1;
                    }
                );
                setOutcomeReports(reports);
            },
            (error) => {
                console.error(error);
                notificationManager.addError(error);
                setOutcomeReports([]);
            }
        );

        setIsLoading(!(result.wasCallable && result.wasSuccessful));
    };

    const disapproveReport = async (report) => {
        notificationManager.clearAllNotifications();

        await API.api_Patch(
            componentName,
            `/outcome_reports/${report.id}/disapprove/`,
            null,
            notificationManager,
            (_response) => {
                setOutcomeReports(outcomeReports.filter(r => r.id !== report.id));

                let reportWeek = report.report_week || report.sample_plan_weeks;
                let successMessage = `Disapproved: ${patientName} ${R.isMonth6Report(reportWeek) ? "6 Months Post" : `Week ${reportWeek}`} Outcome Report`;
                notificationManager.addSuccess(successMessage, { duration: 10 });
            },
            (error) => {
                console.error(error);
                notificationManager.addError(error);
            }
        );
    };

    const disapproveWarning = (report) => {
        notificationManager.clearAllNotifications();

        let text = "WARNING!\n\nDisapproving this report will disallow external organizations and non-reviewers from downloading it.\n\nReport approvers may edit and re-approve the report by clicking Reports in the side bar menu.\n\nPress OK to disapprove.";
        if (window.confirm(text) === true) {
            void disapproveReport(report);
        }
    };

    const deleteReport = async (report) => {
        notificationManager.clearAllNotifications();

        await API.api_Delete(
            componentName,
            `/outcome_reports/${report.id}/`,
            notificationManager,
            (response) => {
                setOutcomeReports(outcomeReports.filter(r => r.id !== report.id));

                let reportWeek = report.report_week || report.sample_plan_weeks;
                let successMessage = `Deleted: ${patientName} ${R.isMonth6Report(reportWeek) ? "6 Months Post" : `Week ${reportWeek}`} Outcome Report`;
                notificationManager.addSuccess(successMessage, { duration: 10 });
            },
            (error) => {
                console.error(error);
                notificationManager.addError(error);
            }
        );
    };

    const deleteWarning = (report) => {
        notificationManager.clearAllNotifications();

        let text = "WARNING!\n\nAre you sure you want to delete this Outcome Report?\n\nThere is no way to recover it once deleted!\n\nPress OK to delete or Cancel to return to editing.";
        if (window.confirm(text) === true) {
            void deleteReport(report);
        }
    };

    const downloadReportPDF = (reportId) => {
        if (reportId) {
            void downloadReports(
                componentName,
                null,
                reportId,
                notificationManager,
                API
            );
        }
    };

    const downloadAllReportPDFs = () => {
        if (patientId) {
            void downloadReports(
                componentName,
                patientId,
                null,
                notificationManager,
                API
            );
        }
    };

    // ---=== useEffects ===--- //

    useEffect(
        () => {
            void api_GetOutcomeReports();
        }, []
    );

    // ---=== Render ===--- //

    const renderStatus = (report) => {
        switch (report.status) {
            case "In Review":
                if (report.valid_approvers === undefined || report.valid_approvers === null || report.valid_approvers.length === 0) {
                    return (
                        <div className=" mix-blend-multiply justify-start items-start inline-flex">
                            <div className="pl-1.5 pr-2 py-0.5 bg-error-50 rounded-2xl justify-center items-center gap-1.5 flex">
                                <div className="w-2 h-2 relative">
                                    <div className="w-1.5 h-1.5 left-[1px] top-[1px] absolute bg-error-500 rounded-full"/>
                                </div>
                                <div className="text-center text-error-700 text-xs font-medium leading-[18px]">Missing Approver</div>
                            </div>
                        </div>
                    );
                }
                break;
            case "Approved":
                return (
                    <div className=" pl-1.5 pr-2 py-0.5 bg-success-50 rounded-2xl justify-center items-center gap-1.5 inline-flex">
                        <div className="w-2 h-2 relative">
                            <div className="w-1.5 h-1.5 left-[1px] top-[1px] absolute bg-success-500 rounded-full"/>
                        </div>
                        <div className="text-center text-success-700 text-xs font-medium leading-[18px]">Approved</div>
                    </div>
                );
        }

        return (
            <div className=" mix-blend-multiply justify-start items-start inline-flex">
                <div className="pl-1.5 pr-2 py-0.5 bg-warning-50 rounded-2xl justify-center items-center gap-1.5 flex">
                    <div className="w-2 h-2 relative">
                        <div className="w-1.5 h-1.5 left-[1px] top-[1px] absolute bg-warning-500 rounded-full"/>
                    </div>
                    <div className="text-center text-warning-700 text-xs font-medium leading-[18px]">{report.status}</div>
                </div>
            </div>
        );
    };

    const renderTable = () => {
        let t = [];

        outcomeReports.forEach(
            (report) => {
                let reportWeek = report.report_week || report.sample_plan_weeks;
                let permissions = report.permissions;

                t.push(
                    <React.Fragment key={`outcome-report-${report.id}`}>
                        <tr id={`outcome-report-${report.id}`} className="whitespace-nowrap border-y-2 even:bg-white odd:bg-white border-gray-200">
                            <td className="whitespace-nowrap border-x-0 px-6 py-3 text-center text-xs font-medium text-gray-500 leading-[18px]">{reportWeek ? R.isMonth6Report(reportWeek) ? "6 Months Post" : reportWeek : "Unselected"}</td>
                            <td className="whitespace-nowrap border-x-0 px-6 py-3 text-center text-xs font-medium text-gray-500 leading-[18px]">{renderStatus(report)}</td>
                            <td className="whitespace-nowrap border-x-0 px-6 py-3 text-center text-xs font-medium text-gray-500 leading-[18px]">{moment.tz(report.prepared_date, moment.tz.guess()).format("MM/DD/YYYY h:mma z")}</td>
                            <td className="whitespace-nowrap border-x-0 content-center text-center px-6 py-3 text-xs font-medium text-gray-500 leading-[18px]">
                                <div className="justify-start items-center inline-flex gap-x-2">
                                    {
                                        report.status === "Not Submitted" && permissions.can_edit && (
                                            <button
                                                className=" px-3.5 py-2 bg-brand-600 hover:bg-brand-700 rounded-lg shadow border border-brand-600 justify-center items-center gap-2 inline-flex cursor-pointer"
                                                onClick={() => goToReportReview(report)}
                                            >
                                                <img className="relative" src="/icons/file-download-02.svg" alt="file-download"/>
                                                <div className="text-white text-sm font-semibold leading-tight">Edit</div>
                                            </button>
                                        )
                                    }
                                    {
                                        permissions.can_download && (
                                            <button
                                                className=" px-3.5 py-2 bg-brand-600 hover:bg-brand-700 rounded-lg shadow border border-brand-600 justify-center items-center gap-2 inline-flex cursor-pointer"
                                                onClick={() => downloadReportPDF(report.id)}
                                            >
                                                <img className="relative" src="/icons/file-download-02.svg" alt="file-download"/>
                                                <div className="text-white text-sm font-semibold leading-tight">Download</div>
                                            </button>
                                        )
                                    }
                                    {
                                        permissions.can_disapprove && (
                                            <button
                                                className="mr-1 px-3.5 py-2 bg-warning-600 hover:bg-warning-700 rounded-lg shadow border border-warning-600 justify-center items-center gap-2 inline-flex cursor-pointer"
                                                onClick={() => disapproveWarning(report)}
                                            >
                                                <img className="relative" src="/icons/file-x-02.svg" alt="file-x-02"/>
                                                <div className="text-white text-sm font-semibold  leading-tight">Disapprove</div>
                                            </button>
                                        )
                                    }
                                    {
                                        permissions.can_delete && (
                                            <button
                                                className=" mr-1 px-3.5 py-2 bg-error-600 hover:bg-error-700 rounded-lg shadow border border-orange-800 justify-center items-center gap-2 inline-flex cursor-pointer"
                                                onClick={() => deleteWarning(report)}
                                            >
                                                <img className="relative" src="/icons/trash-01.svg" alt="trash-01"/>
                                                <div className="text-white text-sm font-semibold leading-tight">Delete</div>
                                            </button>
                                        )
                                    }
                                </div>
                            </td>
                            <td className="whitespace-nowrap border-x-0 px-6 py-3 text-center text-xs font-medium text-gray-500 leading-[18px]"></td>
                            <td className="whitespace-nowrap border-x-0 px-6 py-3 text-center text-xs font-medium text-gray-500 leading-[18px]"></td>
                        </tr>
                    </React.Fragment>
                );
            }
        );

        return t;
    };

    return (
        <PatientProfile
            parentComponentName={componentName}
            // Callbacks
            onPatientData={onPatientData}
        >
            <div className="w-full h-full justify-center items-center inline-flex">
                {
                    isLoading
                        ? (
                            renderLoader(loadingMessage)
                        )
                        : (
                            <React.Fragment>
                                <div className={"bg-white w-[90%] h-[90%] rounded-lg shadow-lg border-2 border-gray-200 flex-col items-end flex"}>
                                    <div className="w-full h-[10%] px-6 pt-5 pb-[19px] justify-start items-center gap-x-1 inline-flex">
                                        <div className="w-[75%] h-full justify-start items-center gap-x-4 inline-flex">
                                            <div className="text-gray-900 text-lg font-medium leading-7 whitespace-nowrap">
                                                Outcome Reports
                                            </div>
                                            {
                                                outcomeReports.length > 0 && (
                                                    <div className="mix-blend-multiply justify-start items-start flex">
                                                        <div className="px-2 py-0.5 bg-brand-50 rounded-2xl justify-center items-center flex">
                                                            <div className="text-center text-brand-800 text-xs font-medium leading-[18px]">
                                                                {`${outcomeReports.filter(report => report.status === "In Review").length} Submitted`}
                                                            </div>
                                                        </div>
                                                    </div>
                                                )
                                            }
                                        </div>
                                        <div className="gap-x-4 inline-flex items-center">
                                            <div className="grow shrink w-full basis-0 relative items-center flex justify-end">
                                                <button
                                                    className="px-4 h-1/2 w-full  py-2.5 max-[1200px]:px-4 max-[1200px]:py-2 bg-white rounded-lg shadow border border-gray-300 hover:bg-gray-100 cursor-pointer justify-center items-center gap-1.5 inline-flex"
                                                    onClick={goToReportCreate}
                                                >
                                                    <img className=" grow shrink h-5 relative" src="/icons/file-plus-03.svg" alt="icon"/>
                                                    <div className="px-0.5 justify-center items-center flex grow shrink">
                                                        <div className="text-gray-700 whitespace-nowrap text-base max-[1200px]:text-xs font-semibold">
                                                            Create New Report
                                                        </div>
                                                    </div>
                                                </button>
                                            </div>
                                            <div className="grow shrink w-full basis-0 h-full items-center flex justify-end">
                                                <button
                                                    className={
                                                        `px-4 py-2.5 max-[1200px]:px-4 max-[1200px]:py-2 rounded-lg shadow border ${
                                                            outcomeReports?.length > 0 && outcomeReports.some(report => report.status === "Approved")
                                                                ? "bg-white border-gray-300 hover:bg-gray-100 cursor-pointer"
                                                                : "bg-gray-100 border-gray-200 cursor-not-allowed"
                                                        } justify-center items-center gap-1.5 flex`
                                                    }
                                                    onClick={downloadAllReportPDFs}
                                                    disabled={!(outcomeReports?.length > 0 && outcomeReports.some(report => report.status === "Approved"))}
                                                >
                                                    <img
                                                        className={
                                                            `grow shrink h-5 relative ${
                                                                outcomeReports?.length > 0 && outcomeReports.some(report => report.status === "Approved")
                                                                    ? ""
                                                                    : "opacity-50"
                                                            }`
                                                        }
                                                        src="/icons/folder-download.svg"
                                                        alt="icon"
                                                    />
                                                    <div className="px-0.5 justify-center items-center flex grow shrink">
                                                        <div
                                                            className={
                                                                `whitespace-nowrap text-base max-[1200px]:text-xs font-semibold leading-normal ${
                                                                    outcomeReports?.length > 0 && outcomeReports.some(report => report.status === "Approved")
                                                                        ? "text-gray-700"
                                                                        : "text-gray-400"
                                                                }`
                                                            }
                                                        >
                                                            Download All
                                                        </div>
                                                    </div>
                                                </button>
                                            </div>

                                        </div>
                                    </div>
                                    {
                                        outcomeReports && outcomeReports.length > 0
                                            ? (
                                                <div className="grid grid-col w-full whitespace-nowrap overflow-y-auto">
                                                    <div className="whitespace-nowrap w-full">
                                                        <div className="whitespace-nowrap w-full inline-block align-middle">
                                                            <div className=" whitespace-nowrap w-full">
                                                                {/*ref={tableRef}*/}
                                                                <table className=" w-full">
                                                                    <thead className="whitespace-nowrap	w-full">
                                                                        <tr className="shadow-[inset_0_1px_0_#d3d3d3,inset_0_-1px_0_#d3d3d3] bg-gray-25 border-gray-200 whitespace-nowrap sticky top-0 z-[2]">
                                                                            <th scope="col" className="sticky top-0 z-[2] whitespace-nowrap	border-x-0 px-6 py-3 text-center text-xs font-medium text-gray-500 bg-gray-300 leading-[18px]">Report Week</th>
                                                                            <th scope="col" className="sticky top-0 z-[2] whitespace-nowrap	border-x-0 px-6 py-3 text-center text-xs font-medium text-gray-500 bg-gray-300 leading-[18px]">Status</th>
                                                                            <th scope="col" className="sticky top-0 z-[2] whitespace-nowrap	border-x-0 px-6 py-3 text-center text-xs font-medium text-gray-500 bg-gray-300 leading-[18px]">Created</th>
                                                                            <th scope="col" className="sticky top-0 z-[2] whitespace-nowrap	border-x-0 px-6 py-3 text-center text-xs font-medium text-gray-500 bg-gray-300 leading-[18px]">Actions</th>
                                                                            <th scope="col" className="sticky top-0 z-[2] whitespace-nowrap	border-x-0 px-6 py-3 text-center text-xs font-medium text-gray-500 bg-gray-300 leading-[18px] text-opacity-0"></th>
                                                                            <th scope="col" className="sticky top-0 z-[2] whitespace-nowrap	border-x-0 px-6 py-3 text-center text-xs font-medium text-gray-500 bg-gray-300 leading-[18px] text-opacity-0"></th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody className="whitespace-nowrap">
                                                                        <React.Fragment>
                                                                            {
                                                                                renderTable()
                                                                            }
                                                                        </React.Fragment>
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )
                                            : (
                                                <div className=" w-full h-full justify-center items-center inline-flex flex-col">
                                                    <p className="px-6 text-gray-900 text-lg font-medium ">No Reports Found</p>
                                                </div>
                                            )
                                    }
                                </div>
                            </React.Fragment>
                        )
                }
            </div>
        </PatientProfile>
    );
};

export default PatientReportReview;
